import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Loader2, Sparkles, Globe, Info, AlertTriangle, CheckCircle2,
} from "lucide-react";
import { generateReport } from "@/lib/api";
import { isQuotaError, quotaErrorPayload } from "@/lib/subscriptionApi";
import { useSubscription } from "@/contexts/SubscriptionContext";
import UpgradeModal from "@/components/UpgradeModal";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { detectAmbiguity, normalizeUrl, nameDomainMismatch } from "@/lib/accountValidation";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

const Field = ({ label, hint, children, testId }) => (
  <div className="flex flex-col gap-2">
    <div className="flex items-baseline justify-between">
      <label className="kpi-label" data-testid={testId ? `${testId}-label` : undefined}>{label}</label>
      {hint && <span className="text-[10px] text-[#525252] font-mono-data">{hint}</span>}
    </div>
    {children}
  </div>
);

export const NewReportForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [upgrade, setUpgrade] = useState(null); // { resource, used, limit, plan } | null
  const { refresh: refreshSub } = useSubscription();
  const [form, setForm] = useState({
    company_name: "",
    website_url: "",
    geography: "",
    industry: "",
    contacts: "",
    pain_points: "",
    past_interactions: "",
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  // --- Live validation signals
  const ambiguity = useMemo(() => detectAmbiguity(form.company_name), [form.company_name]);
  const normalizedSite = useMemo(() => normalizeUrl(form.website_url), [form.website_url]);
  const urlInvalid = form.website_url.trim().length > 0 && !normalizedSite;
  const mismatch = useMemo(
    () => normalizedSite && nameDomainMismatch(form.company_name, normalizedSite.domain),
    [form.company_name, normalizedSite]
  );

  const submit = async () => {
    setLoading(true);
    try {
      const payload = { ...form, website_url: normalizedSite ? normalizedSite.url : "" };
      const data = await generateReport(payload);
      toast.success(`Started · ${form.company_name}`, {
        description: normalizedSite
          ? `Anchored on ${normalizedSite.domain} — ~90 seconds.`
          : "Compiling 21 sections — ~90 seconds.",
      });
      // Quota was charged server-side; refresh the header badge.
      refreshSub();
      navigate(`/report/${data.id}`);
    } catch (err) {
      if (isQuotaError(err)) {
        setUpgrade(quotaErrorPayload(err));
      } else {
        const msg = err?.response?.data?.detail || err.message || "Generation failed";
        toast.error("Could not start generation", { description: msg });
      }
    } finally {
      setLoading(false);
      setConfirmOpen(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.company_name.trim()) {
      toast.error("Company name is required");
      return;
    }
    if (urlInvalid) {
      toast.error("Website URL doesn't look valid", { description: "Use company.com or https://company.com" });
      return;
    }
    // Cross-check: company name ↔ domain. If they don't share any token, ask the user to confirm.
    if (mismatch) {
      setConfirmOpen(true);
      return;
    }
    await submit();
  };

  return (
    <form onSubmit={onSubmit} data-testid="new-report-form" className="bg-[#141414] border border-[#262626] rounded-sm p-6 md:p-8">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="kpi-label mb-2">/ NEW · ACCOUNT INTELLIGENCE</div>
          <h2 className="font-display text-2xl md:text-3xl text-white tracking-tight">Compile a target account</h2>
          <p className="text-sm text-[#A3A3A3] mt-1 max-w-xl">
            Enter a company. RADAR will produce a 21-section executive intelligence report,
            deal opportunities, CXO outreach, and a CRM pipeline entry — in under 90 seconds.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="md:col-span-2">
          <Field label="Company name *" hint="REQUIRED">
            <Input
              data-testid="company-name-input"
              value={form.company_name}
              onChange={set("company_name")}
              placeholder="e.g. Siemens, Boeing, Caterpillar, Schneider Electric"
              className="h-11 bg-[#0A0A0A] border-[#262626] rounded-sm text-base font-mono-data placeholder:text-[#525252] focus-visible:ring-1 focus-visible:ring-[#0A84FF] focus-visible:border-[#0A84FF]"
              autoComplete="off"
            />
          </Field>
        </div>

        {/* Website URL — full width, with smart hint */}
        <div className="md:col-span-2">
          <Field
            label={
              <span className="inline-flex items-center gap-1.5">
                Company website
                <span
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm border border-[#262626] bg-[#0A0A0A] text-[9px] font-mono-data tracking-wider uppercase text-[#737373]"
                  title="Adding the official website helps RADAR anchor research on the correct entity"
                >
                  <Info className="w-2.5 h-2.5"/>
                  Recommended
                </span>
              </span>
            }
            hint={ambiguity.ambiguous ? "STRONGLY RECOMMENDED" : "OPTIONAL"}
            testId="website-url"
          >
            <div className="relative">
              <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#525252] pointer-events-none"/>
              <Input
                data-testid="website-url-input"
                value={form.website_url}
                onChange={set("website_url")}
                placeholder="e.g. siemens.com or https://www.boeing.com"
                className={`h-11 pl-9 bg-[#0A0A0A] rounded-sm text-sm font-mono-data placeholder:text-[#525252] focus-visible:ring-1 focus-visible:ring-[#0A84FF] focus-visible:border-[#0A84FF] ${
                  urlInvalid ? "border-[#FF453A]" : normalizedSite ? "border-[#32D74B]/40" : "border-[#262626]"
                }`}
                autoComplete="off"
                inputMode="url"
              />
              {normalizedSite && !urlInvalid && (
                <CheckCircle2
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#32D74B]"
                  data-testid="website-url-valid-icon"
                />
              )}
            </div>

            {/* Inline helper / ambiguity messaging */}
            {urlInvalid ? (
              <div
                className="mt-2 flex items-start gap-2 text-[12px] text-[#FF453A] leading-relaxed"
                data-testid="website-url-error"
              >
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0"/>
                <span>Doesn't look like a valid URL. Try <span className="font-mono-data">company.com</span> or <span className="font-mono-data">https://company.com</span>.</span>
              </div>
            ) : normalizedSite ? (
              <div
                className="mt-2 flex items-start gap-2 text-[12px] text-[#737373] leading-relaxed"
                data-testid="website-url-anchor-hint"
              >
                <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#32D74B]"/>
                <span>Anchoring research on <span className="text-white font-mono-data">{normalizedSite.domain}</span> — leadership, financials and news will be tied to this entity.</span>
              </div>
            ) : ambiguity.ambiguous ? (
              <div
                className="mt-2 flex items-start gap-2 text-[12px] text-[#FF9F0A] leading-relaxed"
                data-testid="ambiguity-hint"
              >
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0"/>
                <span>{ambiguity.reason} <span className="text-[#A3A3A3]">To improve accuracy, please add the official company website.</span></span>
              </div>
            ) : (
              <div className="mt-2 flex items-start gap-2 text-[12px] text-[#525252] leading-relaxed">
                <Info className="w-3.5 h-3.5 mt-0.5 shrink-0"/>
                <span>Providing the company website improves research precision and reduces ambiguity.</span>
              </div>
            )}
          </Field>
        </div>

        <Field label="Target geography" hint="OPTIONAL">
          <Input
            data-testid="geography-input"
            value={form.geography}
            onChange={set("geography")}
            placeholder="e.g. North America, EMEA, APAC"
            className="h-10 bg-[#0A0A0A] border-[#262626] rounded-sm text-sm placeholder:text-[#525252] focus-visible:ring-1 focus-visible:ring-[#0A84FF]"
          />
        </Field>

        <Field label="Industry segment" hint="OPTIONAL">
          <Input
            data-testid="industry-input"
            value={form.industry}
            onChange={set("industry")}
            placeholder="e.g. Industrial Automation, Aerospace, Auto"
            className="h-10 bg-[#0A0A0A] border-[#262626] rounded-sm text-sm placeholder:text-[#525252] focus-visible:ring-1 focus-visible:ring-[#0A84FF]"
          />
        </Field>

        <Field label="Known contacts" hint="OPTIONAL">
          <Textarea
            data-testid="contacts-input"
            value={form.contacts}
            onChange={set("contacts")}
            placeholder="Names / roles inside the account"
            rows={3}
            className="bg-[#0A0A0A] border-[#262626] rounded-sm text-sm placeholder:text-[#525252] focus-visible:ring-1 focus-visible:ring-[#0A84FF] resize-none"
          />
        </Field>

        <Field label="Known pain points" hint="OPTIONAL">
          <Textarea
            data-testid="pain-points-input"
            value={form.pain_points}
            onChange={set("pain_points")}
            placeholder="Engineering, supply chain, digital, etc."
            rows={3}
            className="bg-[#0A0A0A] border-[#262626] rounded-sm text-sm placeholder:text-[#525252] focus-visible:ring-1 focus-visible:ring-[#0A84FF] resize-none"
          />
        </Field>

        <div className="md:col-span-2">
          <Field label="Past interactions" hint="OPTIONAL">
            <Textarea
              data-testid="past-interactions-input"
              value={form.past_interactions}
              onChange={set("past_interactions")}
              placeholder="Past meetings, RFPs, ongoing deals…"
              rows={2}
              className="bg-[#0A0A0A] border-[#262626] rounded-sm text-sm placeholder:text-[#525252] focus-visible:ring-1 focus-visible:ring-[#0A84FF] resize-none"
            />
          </Field>
        </div>
      </div>

      <div className="flex items-center justify-between mt-7 pt-5 border-t border-[#262626]">
        <div className="flex items-center gap-3 kpi-label">
          <span className="w-1.5 h-1.5 rounded-full bg-[#0A84FF] animate-pulse" />
          <span>Engine · Claude Haiku 4.5</span>
        </div>
        <Button
          type="submit"
          data-testid="generate-report-btn"
          disabled={loading || urlInvalid}
          className="h-11 px-6 rounded-sm bg-[#0A84FF] hover:bg-[#409CFF] text-white font-medium tracking-tight gap-2 transition-colors disabled:opacity-60"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Compiling…</span>
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4" strokeWidth={1.8} />
              <span>Generate Intelligence</span>
            </>
          )}
        </Button>
      </div>

      {/* Name ↔ domain mismatch confirmation */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent
          className="bg-[#141414] border-[#262626] text-white"
          data-testid="mismatch-confirm-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-[#FF9F0A]"/>
              Please confirm this is the correct company
            </DialogTitle>
            <DialogDescription className="text-[#A3A3A3] pt-1">
              The company name and website domain don't seem to match. Generating intelligence
              on the wrong entity will waste a research run.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <div className="bg-[#0A0A0A] border border-[#262626] rounded-sm p-3">
              <div className="kpi-label mb-1">Company</div>
              <div className="text-white text-sm font-mono-data break-words">{form.company_name}</div>
            </div>
            <div className="bg-[#0A0A0A] border border-[#262626] rounded-sm p-3">
              <div className="kpi-label mb-1">Domain</div>
              <div className="text-white text-sm font-mono-data break-words">
                {normalizedSite ? normalizedSite.domain : ""}
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setConfirmOpen(false)}
              data-testid="mismatch-edit-btn"
              className="bg-transparent hover:bg-[#1F1F1F] text-white border border-[#262626]"
            >
              Edit inputs
            </Button>
            <Button
              type="button"
              onClick={submit}
              data-testid="mismatch-confirm-btn"
              disabled={loading}
              className="bg-[#0A84FF] hover:bg-[#409CFF] text-white gap-2"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin"/>}
              Yes, this is correct — generate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <UpgradeModal
        open={!!upgrade}
        onClose={() => setUpgrade(null)}
        resource={upgrade?.resource || "reports"}
        plan={upgrade?.plan}
        used={upgrade?.used}
        limit={upgrade?.limit}
      />
    </form>
  );
};

export default NewReportForm;
